import { motion } from "framer-motion";
import { Container } from "@/components/ui/Container";
import { Button } from "@/components/ui/Button";
import { SectionHeading } from "@/components/ui/SectionHeading";
import { PageHeader } from "@/components/layout/PageHeader";
import { useLanguage } from "@/components/LanguageContext";
import { SEO } from "@/components/layout/SEO";
import { fadeInUp, staggerContainer } from "@/lib/motion";
import { PrecisionTool, Flash, Strategy, Leaf, Eye } from "iconoir-react";

export default function About() {
  const { t } = useLanguage();

  const values = [
    {
      icon: PrecisionTool,
      title: t("Précision", "Precision"),
      desc: t("Rigueur basée sur les données dans chaque analyse, modèle et recommandation.", "Data-driven rigor in every analysis, model, and recommendation."),
    },
    {
      icon: Flash,
      title: t("Impact", "Impact"),
      desc: t("Des solutions conçues pour produire une transformation mesurable et durable.", "Solutions designed to produce measurable, lasting transformation."),
    },
    {
      icon: Strategy,
      title: t("Stratégie", "Strategy"),
      desc: t("Pensée systémique appliquée aux défis complexes territoriaux et de mobilité.", "Systems thinking applied to complex territorial and mobility challenges."),
    },
    {
      icon: Leaf,
      title: t("Durabilité", "Sustainability"),
      desc: t("La responsabilité environnementale intégrée dans chaque projet que nous livrons.", "Environmental responsibility embedded in every project we deliver."),
    },
    {
      icon: Eye,
      title: t("Clarté", "Clarity"),
      desc: t("Communication transparente, livrables clairs et informations actionnables.", "Transparent communication, clear deliverables, and actionable insights."),
    },
  ];

  return (
    <>
      <SEO 
        title={t("À Propos", "About")}
        description={t(
          "Découvrez AVISIUM Solutions Group, un cabinet de conseil multidisciplinaire spécialisé dans l'ingénierie de la mobilité et l'intelligence territoriale.",
          "Learn about AVISIUM Solutions Group, a multidisciplinary consulting firm specializing in mobility engineering and territorial intelligence."
        )}
      />
      <PageHeader 
        overlineFr="À Propos d'AVISIUM"
        overlineEn="About AVISIUM"
        titleFr="Ingénierie de la Mobilité. Façonnement des Territoires."
        titleEn="Engineering Mobility. Shaping Territories."
        descriptionFr="AVISIUM Solutions Group est un cabinet de conseil et de solutions opérant à l'intersection des systèmes de mobilité, de l'ingénierie des infrastructures, de l'intelligence territoriale et de l'innovation numérique."
        descriptionEn="AVISIUM Solutions Group is a consulting and solutions firm operating at the intersection of mobility systems, infrastructure engineering, territorial intelligence, and digital innovation."
      />

      <section className="py-20 lg:py-28 bg-warm-white">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber mb-3">
                {t("Qui Sommes-Nous", "Who We Are")}
              </p>
              <h2 className="text-3xl md:text-4xl font-display font-semibold text-charcoal leading-tight mb-6">
                {t("Un Groupe de Solutions Multidisciplinaire", "A Multidisciplinary Solutions Group")}
              </h2>
              <div className="space-y-4 text-base text-graphite leading-relaxed">
                <p>
                  {t("Fondée avec la conviction que la mobilité durable requiert une intelligence intégrée, AVISIUM réunit l'expertise en planification des transports, ingénierie civile, SIG, science des données et stratégie environnementale.", "Founded with the conviction that sustainable mobility requires integrated intelligence, AVISIUM brings together expertise in transport planning, civil engineering, GIS, data science, and environmental strategy.")}
                </p>
                <p>
                  {t("Nous accompagnons les acteurs publics, les autorités de la ville, les ministères, les organisations internationales et les clients du secteur privé à travers le Maroc et la région.", "We serve public agencies, city authorities, ministries, international development organizations, and private sector clients across Morocco and the wider region.")}
                </p>
                <p>
                  {t("Notre approche est basée sur les données, conçue pour les décideurs, et orientée vers un impact mesurable sur la mobilité urbaine, la qualité des infrastructures et la durabilité territoriale.", "Our approach is rooted in data, designed for decision-makers, and oriented toward measurable impact on urban mobility, infrastructure quality, and territorial sustainability.")}
                </p>
              </div>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="relative h-80 lg:h-[420px] rounded-2xl bg-mineral overflow-hidden shadow-lg"
            >
              <img 
                src="/images/siteweb/image 16.jpg" 
                alt="Mobility Engineering Workspace" 
                className="w-full h-full object-cover"
              />
            </motion.div>
          </div>
        </Container>
      </section>

      <section className="py-20 lg:py-28 bg-mineral/30">
        <Container>
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-amber mb-3">
              {t("Notre Approche", "Our Approach")}
            </p>
            <h2 className="text-3xl md:text-4xl font-display font-semibold text-charcoal leading-tight mb-6">
              {t("Où l'Ingénierie Rencontre l'Intelligence", "Where Engineering Meets Intelligence")}
            </h2>
            <p className="text-base text-graphite leading-relaxed">
              {t("Nous combinons l'ingénierie de terrain avec des analyses avancées. Chaque projet intègre des données spatiales, une modélisation de la mobilité et une conception de scénarios stratégiques — produisant des solutions actionnables qui résistent à l'examen institutionnel et à la complexité du monde réel.", "We combine field-grounded engineering with advanced analytics. Every project integrates spatial data, mobility modeling, and strategic scenario design — producing actionable solutions that stand up to institutional scrutiny and real-world complexity.")}
            </p>
          </motion.div>
        </Container>
      </section>

      <section className="py-20 lg:py-28 bg-warm-white">
        <Container>
          <SectionHeading
            overline={t("Nos Valeurs", "Our Values")}
            title={t("Des Principes qui Pilotent la Performance", "Principles That Drive Performance")}
          />
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5"
          >
            {values.map((v) => (
              <motion.div
                key={v.title}
                variants={fadeInUp}
                className="text-center p-6 rounded-2xl bg-mineral/40 hover:bg-mineral transition-all duration-500"
              >
                <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-xl bg-warm-white text-amber mb-4">
                  <v.icon width={20} height={20} strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold text-charcoal mb-2 font-sans">
                  {v.title}
                </h3>
                <p className="text-xs text-graphite-light leading-relaxed">
                  {v.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </section>

      <section className="py-16 bg-mineral/30">
        <Container>
          <div className="text-center">
            <h2 className="text-2xl font-display font-semibold text-charcoal mb-4">
              {t("Prêt à Travailler Ensemble ?", "Ready to Work Together?")}
            </h2>
            <p className="text-graphite-light mb-8 max-w-lg mx-auto">
              {t("Discutons de la manière dont AVISIUM peut soutenir votre prochain projet de mobilité, d'infrastructure ou d'intelligence territoriale.", "Let's discuss how AVISIUM can support your next mobility, infrastructure, or territorial intelligence project.")}
            </p>
            <Button href="/contact" variant="primary" size="lg">
              {t("Nous Contacter", "Get in Touch")}
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
}
